/**
 * journeyEngine.js
 *
 * Defines the 20-tag bubble vocabulary, maps user preference tags to level
 * content tags, derives a persona, and surfaces recommended levels.
 */

// ─── 20 Bubble Tags ──────────────────────────────────────────────────────────
export const JOURNEY_TAGS = [
  // Goals
  { id: 'improve-pitch',        label: 'Improve Pitch',       cluster: 'Goals',      emoji: '🎯' },
  { id: 'build-confidence',     label: 'Build Confidence',    cluster: 'Goals',      emoji: '💪' },
  { id: 'prepare-performance',  label: 'Perform on Stage',    cluster: 'Goals',      emoji: '🎤' },
  { id: 'meditate-sing',        label: 'Meditative Singing',  cluster: 'Goals',      emoji: '🧘' },
  { id: 'just-explore',         label: 'Just Explore',        cluster: 'Goals',      emoji: '✨' },
  // Style
  { id: 'classical-focus',      label: 'Classical Deepdive',  cluster: 'Style',      emoji: '🎼' },
  { id: 'casual-fun',           label: 'Casual & Fun',        cluster: 'Style',      emoji: '😄' },
  { id: 'deep-practice',        label: 'Deep Practice',       cluster: 'Style',      emoji: '🌊' },
  { id: 'quick-sessions',       label: 'Quick Sessions',      cluster: 'Style',      emoji: '⚡' },
  { id: 'competitive',          label: 'Beat My Best',        cluster: 'Style',      emoji: '🏆' },
  // Focus
  { id: 'ascending',            label: 'Ascending Scales',    cluster: 'Focus',      emoji: '📈' },
  { id: 'descending',           label: 'Descending Flow',     cluster: 'Focus',      emoji: '📉' },
  { id: 'speed',                label: 'Speed & Accuracy',    cluster: 'Focus',      emoji: '🚀' },
  { id: 'breath-control',       label: 'Breath Control',      cluster: 'Focus',      emoji: '🌬️' },
  { id: 'ear-training',         label: 'Ear Training',        cluster: 'Focus',      emoji: '👂' },
  // Experience
  { id: 'absolute-beginner',    label: 'Total Beginner',      cluster: 'Experience', emoji: '🌱' },
  { id: 'some-experience',      label: 'Some Experience',     cluster: 'Experience', emoji: '🎵' },
  { id: 'returning-singer',     label: 'Returning Singer',    cluster: 'Experience', emoji: '🔄' },
  { id: 'trained-musician',     label: 'Trained Musician',    cluster: 'Experience', emoji: '🎓' },
  { id: 'young-learner',        label: 'Young Learner',       cluster: 'Experience', emoji: '🌟' },
];

// ─── Preference → Level-tag mapping ─────────────────────────────────────────
const PREF_TO_LEVEL_TAGS = {
  'improve-pitch':       ['ear-training', 'foundation'],
  'build-confidence':    ['foundation', 'ascending'],
  'prepare-performance': ['full-sargam', 'speed', 'breath-control'],
  'meditate-sing':       ['foundation', 'breath-control'],
  'just-explore':        ['ascending', 'descending', 'ear-training'],
  'classical-focus':     ['full-sargam', 'ear-training'],
  'casual-fun':          ['foundation', 'ascending'],
  'deep-practice':       ['breath-control', 'ear-training'],
  'quick-sessions':      ['foundation', 'ascending'],
  'competitive':         ['speed', 'full-sargam'],
  'ascending':           ['ascending', 'ear-training'],
  'descending':          ['descending', 'ear-training'],
  'speed':               ['speed'],
  'breath-control':      ['breath-control', 'foundation'],
  'ear-training':        ['ear-training', 'ascending'],
  'absolute-beginner':   ['foundation', 'ascending'],
  'some-experience':     ['ascending', 'descending'],
  'returning-singer':    ['descending', 'full-sargam'],
  'trained-musician':    ['full-sargam', 'speed'],
  'young-learner':       ['foundation', 'ascending'],
};

// ─── Persona definitions ─────────────────────────────────────────────────────
const PERSONAS = [
  {
    keys: ['prepare-performance', 'trained-musician', 'competitive'],
    name:  'The Performer',
    icon:  '🎤',
    desc:  'Your path is built for the spotlight — full scales, top speed, total mastery.',
    color: '#e91e63',
  },
  {
    keys: ['meditate-sing', 'breath-control', 'deep-practice'],
    name:  'The Meditator',
    icon:  '🧘',
    desc:  'Slow, intentional practice. Each breath is a step toward inner harmony.',
    color: '#673ab7',
  },
  {
    keys: ['classical-focus', 'returning-singer', 'trained-musician'],
    name:  'The Scholar',
    icon:  '🎼',
    desc:  'Deep in tradition — your journey explores the subtleties of ragas and scales.',
    color: '#1565c0',
  },
  {
    keys: ['just-explore', 'casual-fun', 'young-learner'],
    name:  'The Explorer',
    icon:  '✨',
    desc:  'Light and curious — your journey is wide, joyful, and full of discovery.',
    color: '#00897b',
  },
  {
    keys: ['absolute-beginner', 'build-confidence', 'quick-sessions'],
    name:  'The Newcomer',
    icon:  '🌱',
    desc:  "Perfect foundation first. You'll gain confidence with every session.",
    color: '#43a047',
  },
  {
    keys: ['improve-pitch', 'ear-training', 'ascending'],
    name:  'The Pitch Weaver',
    icon:  '🎯',
    desc:  'Scale by scale, your ear gets sharper. Ascending melodies are your playground.',
    color: '#f57c00',
  },
  {
    keys: ['speed', 'competitive', 'some-experience'],
    name:  'The Challenger',
    icon:  '🚀',
    desc:  'Fast, precise, unstoppable. You push limits every session.',
    color: '#c62828',
  },
  {
    keys: ['descending', 'returning-singer'],
    name:  'The Descender',
    icon:  '📉',
    desc:  'Where most singers rush up, you master the art of coming down with grace.',
    color: '#00838f',
  },
];

/**
 * Returns a persona object { name, icon, desc, color } based on selected tag ids.
 */
export function getJourneyPersona(selectedTags) {
  if (!selectedTags || selectedTags.length === 0) {
    return { name: 'Your Journey', icon: '🎵', desc: 'A personalized path through Indian classical music.', color: '#58CC02' };
  }
  const tagSet = new Set(selectedTags);
  let best = null;
  let bestScore = -1;
  for (const p of PERSONAS) {
    const score = p.keys.filter((k) => tagSet.has(k)).length;
    if (score > bestScore) {
      bestScore = score;
      best = p;
    }
  }
  if (bestScore === 0) {
    return { name: 'Your Journey', icon: '🎵', desc: 'A personalized path crafted just for you.', color: '#58CC02' };
  }
  return { name: best.name, icon: best.icon, desc: best.desc, color: best.color };
}

/**
 * Returns an array of level IDs (up to 5) that best match the selected preference tags.
 * Used for the "For You" badge on the level grid when no journey is set.
 */
export function getRecommendedLevelIds(selectedTags, allLevels) {
  if (!selectedTags || selectedTags.length === 0) return [];

  const wantedLevelTags = new Set();
  for (const pref of selectedTags) {
    (PREF_TO_LEVEL_TAGS[pref] || []).forEach((t) => wantedLevelTags.add(t));
  }

  const scored = allLevels.map((l) => {
    const levelTags = l.tags || [];
    const score = levelTags.filter((t) => wantedLevelTags.has(t)).length;
    return { id: l.id, score };
  });

  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5)
    .map((s) => s.id);
}

// ── Seeded RNG (LCG — deterministic, same tags → same journey) ───────────────
function _hashTags(tags) {
  const str = [...tags].sort().join('|');
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (Math.imul(h, 16777619)) >>> 0;
  }
  return h;
}

function _makeRng(seed) {
  let s = seed >>> 0 || 1;
  return () => {
    s = (Math.imul(1664525, s) + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

/** Weighted shuffle — higher-score items more likely to appear early, seeded. */
function _weightedShuffle(items, scoreOf, rng) {
  // Assign a random key biased by score (score 0 → plain random, score 3 → ~3× more likely first)
  return items
    .map((item) => ({ item, key: rng() / (1 + scoreOf(item)) }))
    .sort((a, b) => a.key - b.key)
    .map(({ item }) => item);
}

/**
 * Generates a personalised 50-level journey ordered by difficulty curve.
 *
 * Difficulty allocation (adjustable by experience tags):
 *   Base:              10 easy · 20 medium · 15 hard · 5 expert = 50
 *   beginner bonus:   +3 easy, -3 hard  → 13 / 20 / 12 / 5
 *   expert bonus:     -3 easy, +3 expert → 7 / 20 / 15 / 8
 *
 * Within each difficulty bucket, levels are weighted-shuffled by tag match,
 * giving tag-aligned levels a higher chance of appearing while still allowing
 * variety on repeat plays (change a single tag → different journey).
 *
 * The first 3 positions are always:
 *   pos 0 → level id 1 (Sa Hold — the universal starting point)
 *   pos 1 → level id 2 (Sa-Re Journey)
 *   pos 2 → level id 3 (Three Note Flow)
 * These are then deducted from the easy quota.
 *
 * @param {string[]} selectedTags    — preference tag IDs from BubbleCloud
 * @param {object[]} allLevels       — full levels array from levels.json
 * @returns {object[]}               — ordered array of 50 level objects
 */
export function generateJourneySequence(selectedTags, allLevels) {
  const JOURNEY_SIZE = 50;
  const FOUNDATION_IDS = [1, 2, 3]; // always first

  if (!selectedTags || selectedTags.length === 0 || !allLevels || allLevels.length === 0) {
    // No prefs: return first JOURNEY_SIZE levels as-is
    return allLevels.slice(0, JOURNEY_SIZE);
  }

  const rng = _makeRng(_hashTags(selectedTags));
  const tagSet = new Set(selectedTags);

  // Build wanted level-tag set from preferences
  const wantedLevelTags = new Set();
  for (const pref of selectedTags) {
    (PREF_TO_LEVEL_TAGS[pref] || []).forEach((t) => wantedLevelTags.add(t));
  }

  // Score function: how well a level matches the user's tags
  const scoreLevel = (l) =>
    (l.tags || []).filter((t) => wantedLevelTags.has(t)).length;

  // ── Difficulty quota (base + experience adjustments) ─────────────────────
  let quota = { easy: 10, medium: 20, hard: 15, expert: 5 };

  if (tagSet.has('absolute-beginner') || tagSet.has('young-learner')) {
    quota = { easy: 13, medium: 22, hard: 11, expert: 4 };
  } else if (tagSet.has('trained-musician')) {
    quota = { easy: 7, medium: 18, hard: 17, expert: 8 };
  } else if (tagSet.has('returning-singer') || tagSet.has('some-experience')) {
    quota = { easy: 8, medium: 20, hard: 16, expert: 6 };
  } else if (tagSet.has('competitive') || tagSet.has('speed')) {
    quota = { easy: 7, medium: 17, hard: 18, expert: 8 };
  }

  // Foundation slots come from the easy quota
  const foundationCount = FOUNDATION_IDS.length;
  quota.easy = Math.max(0, quota.easy - foundationCount);

  // Ensure total = 50 after foundation
  const qTotal = quota.easy + quota.medium + quota.hard + quota.expert + foundationCount;
  if (qTotal < JOURNEY_SIZE) quota.medium += JOURNEY_SIZE - qTotal;

  // ── Partition available levels by difficulty (exclude foundation) ─────────
  const foundationSet = new Set(FOUNDATION_IDS);
  const byDiff = { easy: [], medium: [], hard: [], expert: [] };
  for (const l of allLevels) {
    if (!foundationSet.has(l.id) && byDiff[l.difficulty]) {
      byDiff[l.difficulty].push(l);
    }
  }

  // ── Weighted-shuffle each bucket and take the quota slice ─────────────────
  const pick = (bucket, count) =>
    _weightedShuffle(byDiff[bucket] || [], scoreLevel, rng).slice(0, count);

  const easyPick   = pick('easy',   quota.easy);
  const medPick    = pick('medium', quota.medium);
  const hardPick   = pick('hard',   quota.hard);
  const expertPick = pick('expert', quota.expert);

  // ── Assemble: foundation → easy → medium → hard → expert ─────────────────
  const foundationLevels = FOUNDATION_IDS
    .map((id) => allLevels.find((l) => l.id === id))
    .filter(Boolean);

  const sequence = [
    ...foundationLevels,
    ...easyPick,
    ...medPick,
    ...hardPick,
    ...expertPick,
  ].slice(0, JOURNEY_SIZE);

  return sequence;
}
